/**
 * Carousel Nav — prev/next chevron 驅動 horizontal scroll-snap carousel
 *
 * 對應元件：所有標記 [data-carousel] 的卡片列（scroll-snap-type: x mandatory）
 * 觸發：點同 section 內的 .carousel-nav__btn（prev/next）
 * 行為：list.scrollBy(±(cardWidth + gap), behavior: smooth)，scroll-snap 自動鎖點
 * Disabled：捲到頭 → prev disabled；捲到尾 → next disabled
 * Progress thumb：依 scrollLeft / maxScroll 比例同步 left 偏移（CSS --thumb-offset）
 * Reduced motion：scrollBy 改 behavior: 'auto'，thumb transition 由 CSS @media 關閉
 * 效能：scroll listener RAF 節流 + passive，避免每幀重排
 *
 * Clinic 卡逐張進場（setupCardReveal）：
 *   clinic 卡掛 data-reveal-defer，scroll-reveal.js 不接管 → 由本檔以「carousel 自身為
 *   root」的 IntersectionObserver 在卡片進入水平可見視窗時逐張 reveal。每個 callback
 *   batch 內依 DOM 序給 0,1,2… 的 --data-stagger（reveal.css 換算 idx × stagger-base），
 *   形成清楚的 one-by-one 節奏；counter 每 batch 歸零，故首屏與點「下一組」後都從頭乾淨
 *   依序進場。沿用 reveal.css 的 fade-up 效果與 PRM fallback，不另造動畫機制。
 */

// 共用 util；別名避免與本檔讀一次的布林區域變數 prefersReducedMotion 撞名（語義仍是讀一次）
import { prefersReducedMotion as prefersReducedMotionMQ } from './utils/dom.js';

const TRACK_WIDTH = 120;
const THUMB_WIDTH = 48;
const THUMB_TRAVEL = TRACK_WIDTH - THUMB_WIDTH; // 72px

const setupCarousel = (list) => {
  if (!list) return;

  // prev/next/thumb/nav 全由 list.closest('section') 範圍鎖定（每個 carousel section 內唯一一組
  // carousel-nav，markup 由 _carousel-nav.html partial 保證）。section-scoped 查詢不依賴任何全域
  // ID/屬性值，新增/移除輪播列只需 markup 加/拿掉 data-carousel、不需改本檔（data-target 屬性已於
  // 2026-07-17 隨死碼稽核 A2-15 移除，不影響本檔運作邏輯）。
  const section = list.closest('section') ?? null;
  const prev = section?.querySelector('.carousel-nav__btn--prev') ?? null;
  const next = section?.querySelector('.carousel-nav__btn--next') ?? null;
  if (!prev || !next) return;

  const thumb = section?.querySelector('.carousel-progress__thumb') ?? null;
  const nav = section?.querySelector('.carousel-nav') ?? null;

  // #4：內容未溢出（無左右可捲）→ 隱藏整組 carousel-nav（chevron + progress），溢出才顯示。
  // 用 hidden 屬性切換（carousel-nav.css 有 .carousel-nav[hidden]{display:none} 守衛，
  // 因 .carousel-nav 是 display:inline-flex 原生 hidden 會失效）。+1 容錯 sub-pixel 誤差。
  // 不覆寫 ≤768 的 display:none（該斷點 media query 仍贏：hidden 拿掉後 media 接管隱藏）。
  const syncNavVisibility = () => {
    if (!nav) return;
    const noOverflow = list.scrollWidth <= list.clientWidth + 1;
    nav.hidden = noOverflow;
  };

  const prefersReducedMotion = prefersReducedMotionMQ();
  const scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

  // 計算單張卡 + gap 寬度，作為 scrollBy 步距
  const getScrollDistance = () => {
    const card = list.querySelector(':scope > *');
    if (!card) return 400;
    const cardWidth = card.offsetWidth;
    const gap = parseInt(getComputedStyle(list).gap, 10) || 20;
    return cardWidth + gap;
  };

  prev.addEventListener('click', () => {
    list.scrollBy({ left: -getScrollDistance(), behavior: scrollBehavior });
  });

  next.addEventListener('click', () => {
    list.scrollBy({ left: getScrollDistance(), behavior: scrollBehavior });
  });

  // 同步 disabled + progress thumb
  const updateState = () => {
    const { scrollLeft, scrollWidth, clientWidth } = list;
    // 不可捲動（單卡群組 lipolysis/recovery/regenerative，scrollWidth <= clientWidth）→ 兩顆 chevron
    // 皆顯式 disabled（不靠邊界算術巧合；nav 另由 syncNavVisibility 整組 hidden）。可捲動才按頭/尾判。
    const notScrollable = scrollWidth <= clientWidth + 1; // +1 容錯 sub-pixel
    prev.disabled = notScrollable || scrollLeft <= 0;
    next.disabled = notScrollable || scrollLeft + clientWidth >= scrollWidth - 1; // -1 容錯像素誤差

    if (thumb) {
      const maxScroll = scrollWidth - clientWidth;
      const ratio = maxScroll > 0 ? scrollLeft / maxScroll : 0;
      const offset = Math.round(ratio * THUMB_TRAVEL);
      thumb.style.setProperty('--thumb-offset', `${offset}px`);
    }
  };

  // RAF 節流：scroll 高頻事件每幀只更新一次
  let rafId = null;
  const onScroll = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      updateState();
      rafId = null;
    });
  };

  list.addEventListener('scroll', onScroll, { passive: true });

  // resize：重判 disabled/thumb + nav 顯隱（斷點切換可能讓溢出狀態反轉）。
  // 同一 handler 綁兩件事，避免重複 listener；pagehide 清理。
  const onResize = () => {
    updateState();
    syncNavVisibility();
  };
  window.addEventListener('resize', onResize);
  window.addEventListener('pagehide', () => {
    window.removeEventListener('resize', onResize);
    list.removeEventListener('scroll', onScroll);
  }, { once: true });

  // 初始化（DOM ready 後 layout 已穩定，scrollWidth 可信；render-cards 已先於本檔填卡）
  updateState();
  syncNavVisibility();
};

/**
 * Clinic 卡逐張進場。沿用 [data-reveal] 的 CSS 進場（reveal.css），本檔只負責
 * 「何時 reveal + 給第幾個的 stagger 序號」。三段式機制（兩個 observer + 一個安全網）：
 *
 *   1) sectionObserver（root = 頁面視窗）：偵測 .clinics 區塊「垂直」捲進視窗。
 *      未進視窗前不 reveal 任何卡片 —— 否則 carousel-root observer 在頁面載入當下
 *      就會判定首屏 3~4 張水平可見而提前 reveal（此時區塊還在折線下方），使用者捲到
 *      時動畫早已結束。section 進場後才 arm 第 2 段。
 *   2) cardObserver（root = carousel 容器）：卡片「水平」捲入可見視窗才逐張 reveal。
 *      首屏多張可見 → 一個 callback batch 內依 DOM 序給 0,1,2…（counter 每 batch 歸零）
 *      → 乾淨的 0 / stagger-base / 2×base… 串接；點「下一組」捲入的新一批同樣從 0
 *      重新串接，所以首屏與切組都是清楚的 one-by-one。
 *   3) 安全網（scrolledPast，2026-07-14 補，修「診所區塊滑動時偶爾完全不出現」）：
 *      IntersectionObserver 的進場判定依賴瀏覽器在門檻跨越當下真的算出一個中繼影格；使用者
 *      快速 fling／連續 PageDown／End 鍵／錨點跳轉等大位移捲動時，畫面可能整段從「區塊在
 *      視窗下方（交集比例 0）」跳到「區塊已捲出視窗上方（交集比例回到 0）」，起訖都是 0、
 *      中間那段「比例 ≥ 門檻」的狀態從未被瀏覽器算過一次 → sectionObserver 從未觸發 →
 *      cardObserver 從未被 arm → 卡片永久卡在 reveal.css 的 [data-reveal] 基態 opacity:0
 *      （實測：Playwright 用單次大位移 page.mouse.wheel() 或 scrollTo(behavior:'instant')
 *      皆可穩定重現整區塊完全不出現）。scroll-reveal.js 對一般 [data-reveal] 元素本就有三層
 *      保險（on-load 視口內立即顯示 + 頁底 flush + 3s 全域逾時），但本檔的 defer 卡片刻意排除
 *      在那三層之外（見檔頭「委派進場」段，避免兩個 reveal 來源搶寫同一元素）——代價是完全
 *      沒有備援，兩段式 observer 一旦同時被跳過就無人接手。故在本檔內自建對等安全網，只管
 *      defer 卡片：監聽 scroll（rAF 節流，與 scroll-reveal.js flushBottom 同款寫法），一旦
 *      偵測到 section 下緣已捲出視窗上緣（getBoundingClientRect().bottom <= 0——不論正常
 *      速度、fling 或程式化跳頁，只要視窗「完整捲過」這個區塊一次此條件必為真，且只有真的被
 *      跳過、殘卡仍存在時才會在這條件成立的當下還有事可做），就把殘留未進場的卡直接補上
 *      is-revealed + is-revealed--instant（無 transition，比照本檔橫滑補跑的既有哲學：使用者
 *      已經捲過去了，沒有「進場中」畫面可言，補顯示比繼續空等正確）。安全網刻意不用固定秒數
 *      timeout 兜底：本區塊在頁面中段偏下，若比照「N 秒後不論位置強制顯示」，正常速度捲動的
 *      使用者反而會在抵達本區塊前就被提前 reveal、等於拿掉進場動畫；改用「捲過去了沒」這個
 *      幾何條件，正常速度捲動時恆為 false（不會搶在兩段式 observer 前面動作），只有真的被
 *      跳過時才會是 true——是直接對應失效條件本身的修法，不是加大 timeout 的治標手段。
 *
 * REVEAL_RATIO 0.4：卡片在 carousel 內露出 ~40% 才算進場，避免右緣 sliver 提前觸發。
 * REVEAL_LEAD_MARGIN：cardObserver callback 是非同步排程，不保證跟捲動位置同步；使用者手動
 *   橫滑（尤其快速 fling）時卡片常常先「捲進」實體版位，callback 才觸發、520ms（--motion-
 *   reveal-md）淡入 transition 還沒跑完，形成「卡格在但整張卡 opacity:0」的可視空白窗口。
 *   在 cardObserver 的 root 邊界左右各加水平提前量，讓判定進場的時機提早於卡片實際捲入
 *   可視窗，動畫有機會提前開始甚至跑完。不動 REVEAL_RATIO（進場所需的可見比例門檻不變，
 *   只是判定發生的捲動位置提前）。
 *
 * is-revealed--instant（瞬間顯示，跳過 transition）：REVEAL_LEAD_MARGIN 只是「固定像素」
 *   提前量，對抗的卻是使用者橫滑「原則上無上限」的速度——393px 手機視窗換算的 30% margin
 *   只有 ~118px，要蓋滿 520ms 淡入，捲動速度上限僅約 227px/s，遠低於真實 fling（實測量級
 *   可達 2000-2500px/s）。單靠加大 margin 治標不治本：要在最快 fling 下仍安全，margin 需求
 *   會逼近甚至超過整條軌道總寬，等同要求一開始就把所有卡算進判定範圍。因此改用「首批動畫、
 *   後續瞬間」：cardObserver 第一次實際 reveal 到卡片的 callback（section 剛垂直進場、arm
 *   當下已在可視窗內的卡，不受滑動速度影響）維持原本 520ms fade-up；此後任何一次觸發都必為
 *   使用者橫滑滑出來的卡，改走 reveal.css 的 transition:none（沿用同檔 [data-reveal=
 *   "line-draw"] 既有手法），duration 歸零後不再需要跟任何捲動速度賽跑，是唯一在數學上能
 *   保證不出現 opacity 中繼態空白的做法。第 3 段安全網補顯示時沿用同一 is-revealed--instant
 *   class，非另造機制。
 */
const REVEAL_RATIO = 0.4;
const REVEAL_LEAD_MARGIN = '0px 30% 0px 30%';

const setupCardReveal = (listSelector) => {
  const list = document.querySelector(listSelector);
  if (!list) return;

  const section = list.closest('section') ?? list;
  const cards = list.querySelectorAll('[data-reveal-defer]');
  if (!cards.length) return;

  const prefersReducedMotion = prefersReducedMotionMQ();

  // PRM / 老瀏覽器：直接顯示，不做逐張動畫（與 scroll-reveal.js 直通分支冪等一致）
  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    cards.forEach((card) => card.classList.add('is-revealed'));
    return;
  }

  // 第 2 段：carousel-root observer，卡片水平捲入即逐張 reveal
  // firstBatchDone：只有「第一次」實際 reveal 到卡片的 callback（section 剛垂直進場、arm
  // 當下已在可視窗內的卡，不受滑動速度影響）走原本 520ms fade-up；此後任何一次觸發都必為
  // 使用者橫滑滑出來的卡，改走瞬間顯示（is-revealed--instant，見 reveal.css），不再需要跟
  // 捲動速度賽跑。旗標只在「本批真的有卡片進場」時才翻轉，避免空批次（0 張 intersecting）
  // 誤把下一批真正的首批消耗掉。
  let firstBatchDone = false;
  const cardObserver = new IntersectionObserver((entries, obs) => {
    // 取出本批真正進場的卡片，依水平位置（offsetLeft 左→右）排序，序號從 0 起算 →
    // 同批多張時形成 0 / base / 2×base… 的乾淨依序進場節奏
    const visible = entries
      .filter((entry) => entry.isIntersecting)
      .sort((a, b) => a.target.offsetLeft - b.target.offsetLeft);
    if (!visible.length) return;

    const isFirstBatch = !firstBatchDone;
    firstBatchDone = true;

    visible.forEach((entry, batchIdx) => {
      const card = entry.target;
      card.style.setProperty('--data-stagger', batchIdx);
      // is-revealed 與 is-revealed--instant 同一次 classList.add() 一起加上，避免先套用
      // 舊 transition 再跳變（瀏覽器只會做一次樣式重算）。
      if (isFirstBatch) {
        card.classList.add('is-revealed');
      } else {
        card.classList.add('is-revealed', 'is-revealed--instant');
      }
      obs.unobserve(card);
    });
  }, {
    root: list,            // 以 carousel 自身為視窗 → 水平捲入即觸發
    threshold: REVEAL_RATIO,
    rootMargin: REVEAL_LEAD_MARGIN, // 水平提前量，補償 callback 延遲 + transition 與橫滑/fling 的競速（見上方檔頭）
  });

  // 第 1 段：section 垂直進場才 arm 第 2 段（避免折線下方提前跑完動畫）
  const sectionObserver = new IntersectionObserver((entries, obs) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    cards.forEach((card) => cardObserver.observe(card));
    obs.disconnect();      // 一次性：section 進場後 sectionObserver 即功成身退
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -10% 0px',   // 與 scroll-reveal.js 全站節奏一致
  });

  sectionObserver.observe(section);

  /* ---------- 第 3 段：安全網（scrolledPast）----------
   * 見上方檔頭「3) 安全網」說明。只在「section 已完整捲出視窗上緣」時動作，寫法比照
   * scroll-reveal.js 的 flushBottom/onScroll（rAF 節流 + 完成後自行移除 listener）。
   */
  const remainingDeferCards = () => list.querySelectorAll('[data-reveal-defer]:not(.is-revealed)');

  const flushIfScrolledPast = () => {
    const remaining = remainingDeferCards();
    if (!remaining.length) {
      window.removeEventListener('scroll', onScrolledPast);
      return;
    }
    if (section.getBoundingClientRect().bottom <= 0) {
      remaining.forEach((card, idx) => {
        card.style.setProperty('--data-stagger', idx);
        card.classList.add('is-revealed', 'is-revealed--instant');
        cardObserver.unobserve(card);
      });
      firstBatchDone = true;
      window.removeEventListener('scroll', onScrolledPast);
    }
  };

  let pastTicking = false;
  function onScrolledPast() {
    if (pastTicking) return;
    pastTicking = true;
    requestAnimationFrame(() => {
      flushIfScrolledPast();
      pastTicking = false;
    });
  }

  window.addEventListener('scroll', onScrolledPast, { passive: true });
  // 立即補查一次（不等下一次 scroll 事件）：涵蓋本函式執行當下 section 就已經捲出視窗上緣的
  // 情境（bfcache 還原、hash 錨點跳過本區塊、初始化時機晚於使用者已完成的捲動）。
  requestAnimationFrame(flushIfScrolledPast);

  // cleanup：頁面卸載 / bfcache 時斷開觀察，避免殘留 observer / listener
  window.addEventListener('pagehide', () => {
    sectionObserver.disconnect();
    cardObserver.disconnect();
    window.removeEventListener('scroll', onScrolledPast);
  }, { once: true });
};

// 全站輪播列：markup 標記 [data-carousel] 即自動綁定（每個 list 由 closest('section') 內找 nav/thumb/prev/next）。
// 收斂原 10 個 hard-coded setupCarousel('…') 為單一 loop，涵蓋：首頁診所 .clinic-list / 專欄 .article-list、
// article-detail 相關專欄 .article-related__list、products 7 分類群組 .products-group__grid--*（含 lipolysis/
// recovery/regenerative 單卡群組 —— updateState 把兩顆 chevron 設 disabled、syncNavVisibility 整組 nav hidden）。
// 非該頁時 querySelectorAll 回空集合自動 no-op，全站安全；新增/移除輪播列只需在 markup 加/拿掉 data-carousel。
document.querySelectorAll('[data-carousel]').forEach((list) => setupCarousel(list));

// clinic 卡逐張進場（article 卡仍走 scroll-reveal.js 既有 stagger，不動）
setupCardReveal('.clinic-list');
